import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { setSessionTokenForUploads } from '../utils/uploadDocument';

const API = '/api';

// ── Bearer-token fallback (survives proxy cookie stripping) ──────────────────
// The production edge (Cloudflare in front of *.emergent.host) can drop the
// session cookie, which would 401 every authenticated request and log the user
// out repeatedly. To make auth resilient we persist the session token returned
// by /auth/me and attach it as `Authorization: Bearer` on every request, so
// auth still works even when the cookie never reaches the origin.
const TOKEN_KEY = 'frx_token';

export function getStoredToken() {
  try { return localStorage.getItem(TOKEN_KEY) || null; } catch { return null; }
}
function storeToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch { /* ignore */ }
}

// Install once at module load: every request to OUR api carries the cookie AND
// the bearer token (whichever survives the proxy authenticates the request).
axios.interceptors.request.use((config) => {
  const url = config.url || '';
  const backend = process.env.REACT_APP_BACKEND_URL || '';
  const isOwnApi = url.startsWith('/api') || url.includes('/api/') || (backend && url.startsWith(backend));
  if (isOwnApi) {
    config.withCredentials = true;
    const token = getStoredToken();
    if (token && !config.headers?.Authorization) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // Restore from localStorage immediately to prevent flash-redirect
  const [user, setUserState] = useState(() => {
    try {
      const cached = localStorage.getItem('frx_user');
      return cached ? JSON.parse(cached) : null;
    } catch { return null; }
  });
  const [loading, setLoading] = useState(true);

  // Wrap setUser to also persist to localStorage.
  // Feb 2026 · IMPORTANT — we intentionally do NOT clear the bearer
  // token (`frx_token`) when userData is null. Doing so caused a
  // cross-tab logout cascade: every time the user opened a new tab
  // from a `target="_blank"` link (BD Funnel, Outreach filters, etc.),
  // the fresh tab's AuthProvider ran `checkAuth()`, and any transient
  // failure of `/auth/me` (Cloudflare cold-start, dropped cookie,
  // 5xx, network wobble) would remove `frx_token` from localStorage —
  // which is shared origin-wide — killing auth on the original tab
  // too. The token is only cleared on explicit `logout()` now.
  const setUser = useCallback((userData) => {
    setUserState(userData);
    if (userData) {
      localStorage.setItem('frx_user', JSON.stringify(userData));
    } else {
      localStorage.removeItem('frx_user');
      // storeToken(null) is INTENTIONALLY not called here.
    }
  }, []);

  const checkAuth = useCallback(async () => {
    // Feb 2026 (v5) — resilient auth heartbeat.
    // On production behind Cloudflare, a single /auth/me can 401 for many
    // reasons that AREN'T "your session is dead":
    //   • stale cookie served by an edge that lost sync with origin
    //   • cookie value corrupted / truncated by a proxy
    //   • cookie was for a previous deployment's session_token before
    //     we rolled Bearer as a fallback
    // Previously any 401 → immediate logout. Now: on a 401, if we have a
    // Bearer token in localStorage, retry ONCE with an EXPLICIT Authorization
    // header and NO credentials (bypass the stale cookie). Only clear the
    // user if that retry also 401s — that's a definitive signal the token
    // itself is bad.
    const tryCall = async (opts) => axios.get(`${API}/auth/me`, opts);
    try {
      let response;
      try {
        response = await tryCall({ withCredentials: true });
      } catch (e) {
        const status = e?.response?.status;
        const bearer = getStoredToken();
        if (status === 401 && bearer) {
          // Retry with explicit Bearer, WITHOUT credentials so a stale
          // cookie can't shadow the fresh token.
          response = await tryCall({
            withCredentials: false,
            headers: { Authorization: `Bearer ${bearer}` },
          });
        } else {
          throw e;
        }
      }
      setUser(response.data);
      // Persist the session_token so it survives reloads and can be used as a
      // Bearer fallback on requests where the proxy strips the cookie.
      if (response.data?.session_token) {
        storeToken(response.data.session_token);
        setSessionTokenForUploads(response.data.session_token);
      }
    } catch (e) {
      // Feb 2026 · Only clear the cached user on a DEFINITIVE 401
      // (i.e., both the primary call AND the Bearer-only retry failed).
      // Any other failure (network drop, 5xx, Cloudflare 522/524, CORS
      // hiccup on tab bootstrap) is transient — keep the cached user
      // state so a new tab opened via `target="_blank"` doesn't cascade
      // a logout to the original tab through shared localStorage.
      const status = e?.response?.status;
      if (status === 401) {
        setUser(null);
        // Only NOW clear the stored token — it's definitively dead.
        storeToken(null);
      }
      // else: leave the previously-cached user in place; the next
      // authenticated request will re-verify implicitly.
    } finally {
      setLoading(false);
    }
  }, [setUser]);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    const isAuthCallback = window.location.hash?.includes('session_id=') 
      || window.location.search?.includes('session_id=')
      || window.location.pathname === '/auth/callback';
    if (isAuthCallback) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // Feb 2026 (v5) — background heartbeat: quietly hit /auth/me every 30
  // minutes so the sliding-refresh keeps the session alive for as long as
  // the tab stays open. Without this, a tab left open for 4-5 days
  // straight would eventually cross the 3-day-remaining refresh threshold
  // AFTER expiry and get logged out on the next action.
  useEffect(() => {
    if (!user) return;
    const HEARTBEAT_MS = 30 * 60 * 1000; // 30 min
    const id = setInterval(() => {
      // Silent: swallow errors so a transient network blip doesn't force
      // any user-visible change. checkAuth already handles 401 gracefully.
      checkAuth();
    }, HEARTBEAT_MS);
    return () => clearInterval(id);
  }, [user, checkAuth]);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch { /* ignore */ }
    // Explicit logout DOES clear the token — the cross-tab cascade
    // guard only applies to transient checkAuth failures.
    storeToken(null);
    setSessionTokenForUploads(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
